import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronRight } from 'react-feather';
import type { CSSObjectWithLabel } from 'react-select';
import Select from 'react-select';
import type { FieldErrors } from 'react-hook-form';
import type { ExtractionQuestion, AnswerType } from '@trinity/chat';
import { AnswerTypeEnum, AnswerTypeLabels } from '@trinity/chat';
import { Trash } from '../icons/Trash';
import { Textarea } from '../presentation/Textarea';
import { InfoTooltip } from '../presentation/InfoTooltip';
import { Chip, ChipList } from './Chip';
import { StringArrayEditor } from './StringArrayEditor';
import { KeywordsEditor } from './KeywordsEditor';

type AnswerTypeOption = { value: AnswerType; label: string };

const ANSWER_TYPE_OPTIONS: AnswerTypeOption[] = AnswerTypeEnum.options.map((value) => ({
  value,
  label: AnswerTypeLabels[value],
}));

type Props = {
  question: ExtractionQuestion;
  onChange: (updated: ExtractionQuestion) => void;
  onRemove: () => void;
  editing: boolean;
  errors?: FieldErrors<ExtractionQuestion>;
};

export const ExtractionQuestionRow = ({ question, onChange, onRemove, editing, errors }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      onChange({ ...question, [field]: value } as ExtractionQuestion);
    },
    [question, onChange]
  );

  const handleAnswerTypeChange = useCallback(
    (newType: AnswerType) => {
      const base = {
        questionText: question.questionText,
        label: question.label,
        searchQuery: question.searchQuery,
        keywords: question.keywords,
        extractionGuidance: question.extractionGuidance,
        answerType: newType,
      };

      switch (newType) {
        case 'free_text':
        case 'yes_no':
          onChange(base as ExtractionQuestion);
          break;
        case 'single_select':
        case 'multi_select':
        case 'ranked_list':
          onChange({ ...base, options: [] } as unknown as ExtractionQuestion);
          break;
        case 'numeric':
          onChange({ ...base, unit: null } as unknown as ExtractionQuestion);
          break;
        case 'numeric_range':
          onChange({ ...base, ranges: [] } as unknown as ExtractionQuestion);
          break;
        case 'likert':
          onChange({ ...base, scale: [] } as unknown as ExtractionQuestion);
          break;
      }
    },
    [question, onChange]
  );

  const answerTypeValue = ANSWER_TYPE_OPTIONS.find((o) => o.value === question.answerType) ?? null;
  const summary = `${question.label || '(Untitled)'}`;

  return (
    <RowRoot>
      <RowHeader>
        <CollapseToggle onClick={() => setExpanded(!expanded)} type="button">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </CollapseToggle>
        <RowSummary>{summary}</RowSummary>
        {editing && <Trash onClick={onRemove} size={16} />}
      </RowHeader>

      {expanded && !editing && (
        <RowBody>
          <ReadOnlyField>
            {/* <FieldLabel>Question Text</FieldLabel> */}
            <ReadOnlyValue>{question.questionText || '—'}</ReadOnlyValue>
          </ReadOnlyField>

          <ReadOnlyField>
            <FieldLabel>Response Type</FieldLabel>
            <ReadOnlyValue>{AnswerTypeLabels[question.answerType]}</ReadOnlyValue>
          </ReadOnlyField>

          {renderReadOnlyTypeFields(question)}

          {question.extractionGuidance && (
            <ReadOnlyField>
              <FieldLabel>Extraction Guidance</FieldLabel>
              <ReadOnlyValue>{question.extractionGuidance}</ReadOnlyValue>
            </ReadOnlyField>
          )}

          {/* {question.searchQuery && (
            <ReadOnlyField>
              <FieldLabel>Search Query</FieldLabel>
              <ReadOnlyValue>{question.searchQuery}</ReadOnlyValue>
            </ReadOnlyField>
          )}

          {question.keywords && question.keywords.length > 0 && (
            <ReadOnlyField>
              <FieldLabel>Keywords</FieldLabel>
              <ChipList>
                {question.keywords.map((kw, i) => (
                  <Chip key={`${kw}-${i}`}>{kw}</Chip>
                ))}
              </ChipList>
            </ReadOnlyField>
          )} */}
        </RowBody>
      )}

      {expanded && editing && (
        <RowBody>
          <FieldGroup>
            <FieldLabel>Label *</FieldLabel>
            <Textarea
              value={question.label}
              onChange={(e) => handleFieldChange('label', e.target.value)}
              placeholder="Short label"
              minRows={1}
              maxRows={1}
            />
            {errors?.label && <FieldError>{errors.label.message}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Question Text *</FieldLabel>
            <Textarea
              value={question.questionText}
              onChange={(e) => handleFieldChange('questionText', e.target.value)}
              placeholder="Full question text"
              minRows={2}
            />
            {errors?.questionText && <FieldError>{errors.questionText.message}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Answer Type *</FieldLabel>
            <Select
              value={answerTypeValue}
              options={ANSWER_TYPE_OPTIONS}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 8 }) as CSSObjectWithLabel }}
              onChange={(v) => {
                if (v) handleAnswerTypeChange(v.value);
              }}
            />
          </FieldGroup>

          {/* Type-specific fields */}
          {renderTypeSpecificFields(question, handleFieldChange, errors)}

          <FieldGroup>
            <FieldLabel>Extraction Guidance</FieldLabel>
            <Textarea
              value={question.extractionGuidance ?? ''}
              onChange={(e) => handleFieldChange('extractionGuidance', e.target.value || null)}
              placeholder="Instructions for extraction"
              minRows={2}
            />
          </FieldGroup>

          {/* Advanced toggle */}
          {/* <AdvancedToggle onClick={() => setShowAdvanced(!showAdvanced)} type="button">
            {showAdvanced ? 'Hide' : 'Show'} Advanced Options
          </AdvancedToggle> */}

          {showAdvanced && (
            <AdvancedSection>
              <FieldGroup>
                <FieldLabelRow>
                  <FieldLabel>Search Query</FieldLabel>
                  <InfoTooltip text="Used to determine which parts of the transcript to search when answering this question. Will be auto-generated if left empty." />
                </FieldLabelRow>
                <Textarea
                  value={question.searchQuery ?? ''}
                  onChange={(e) => handleFieldChange('searchQuery', e.target.value || null)}
                  placeholder="Optimized search query"
                  minRows={1}
                  maxRows={1}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabelRow>
                  <FieldLabel>Keywords</FieldLabel>
                  <InfoTooltip text="Keywords used to identify relevant sections of the transcript for answering this question. Will be auto-generated if left empty." />
                </FieldLabelRow>
                <KeywordsEditor
                  value={question.keywords ?? []}
                  onChange={(v) => handleFieldChange('keywords', v.length > 0 ? v : null)}
                  placeholder="Add keyword..."
                />
              </FieldGroup>
            </AdvancedSection>
          )}
        </RowBody>
      )}
    </RowRoot>
  );
};

ExtractionQuestionRow.displayName = 'ExtractionQuestionRow';

// ─── Read-only type-specific field renderer ──────────────────────────────────

function renderReadOnlyTypeFields(question: ExtractionQuestion): React.ReactNode {
  switch (question.answerType) {
    case 'single_select':
    case 'multi_select':
    case 'ranked_list':
      return question.options.length > 0 ? (
        <ReadOnlyField>
          <FieldLabel>Options</FieldLabel>
          <ChipList>
            {question.options.map((opt) => (
              <Chip key={opt.id}>{opt.label}</Chip>
            ))}
          </ChipList>
        </ReadOnlyField>
      ) : null;
    case 'numeric':
      return question.unit ? (
        <ReadOnlyField>
          <FieldLabel>Unit</FieldLabel>
          <ReadOnlyValue>{question.unit}</ReadOnlyValue>
        </ReadOnlyField>
      ) : null;
    case 'numeric_range':
      return question.ranges.length > 0 ? (
        <ReadOnlyField>
          <FieldLabel>Ranges</FieldLabel>
          <ChipList>
            {question.ranges.map((r) => (
              <Chip key={r.id}>{r.label}</Chip>
            ))}
          </ChipList>
        </ReadOnlyField>
      ) : null;
    case 'likert':
      return question.scale.length > 0 ? (
        <ReadOnlyField>
          <FieldLabel>Scale</FieldLabel>
          <ChipList>
            {question.scale.map((s) => (
              <Chip key={s.id}>{s.label}</Chip>
            ))}
          </ChipList>
        </ReadOnlyField>
      ) : null;
    default:
      return null;
  }
}

// ─── Edit-mode type-specific field renderer ──────────────────────────────────

function renderTypeSpecificFields(
  question: ExtractionQuestion,
  handleFieldChange: (field: string, value: unknown) => void,
  errors?: FieldErrors<ExtractionQuestion>
): React.ReactNode {
  switch (question.answerType) {
    case 'single_select':
    case 'multi_select':
    case 'ranked_list':
      return (
        <FieldGroup>
          <FieldLabel>Options *</FieldLabel>
          <StringArrayEditor
            value={question.options}
            onChange={(v) => handleFieldChange('options', v)}
            placeholder="Add option..."
          />
          {errors && 'options' in errors && errors.options?.message && (
            <FieldError>{errors.options.message as string}</FieldError>
          )}
        </FieldGroup>
      );
    case 'numeric':
      return (
        <FieldGroup>
          <FieldLabel>Unit</FieldLabel>
          <Textarea
            value={question.unit ?? ''}
            onChange={(e) => handleFieldChange('unit', e.target.value || null)}
            placeholder="e.g. kg, USD, %"
            minRows={1}
            maxRows={1}
          />
        </FieldGroup>
      );
    case 'numeric_range':
      return (
        <FieldGroup>
          <FieldLabel>Ranges *</FieldLabel>
          <StringArrayEditor
            value={question.ranges}
            onChange={(v) => handleFieldChange('ranges', v)}
            placeholder="Add range (e.g. 0-10)..."
          />
          {errors && 'ranges' in errors && errors.ranges?.message && (
            <FieldError>{errors.ranges.message as string}</FieldError>
          )}
        </FieldGroup>
      );
    case 'likert':
      return (
        <FieldGroup>
          <FieldLabel>Scale *</FieldLabel>
          <StringArrayEditor
            value={question.scale}
            onChange={(v) => handleFieldChange('scale', v)}
            placeholder="Add scale point..."
          />
          {errors && 'scale' in errors && errors.scale?.message && (
            <FieldError>{errors.scale.message as string}</FieldError>
          )}
        </FieldGroup>
      );
    case 'free_text':
    case 'yes_no':
    default:
      return null;
  }
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const RowRoot = styled.div`
  border: 1px solid var(--sec-01, #ccc);
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
  background: var(--white, #fff);
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: var(--gray-d, #666);
`;

const RowSummary = styled.div`
  flex: 1;
  font-size: 15px;
  font-family: var(--font-semibold);
  color: var(--black);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowBody = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-family: var(--font-semibold);
  color: var(--gray-d, #555);
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldError = styled.span`
  font-size: 12px;
  color: var(--red, #d32f2f);
`;

const AdvancedToggle = styled.button`
  font-size: 13px;
  font-family: var(--font-semibold);
  color: var(--pri-02);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const AdvancedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px dashed var(--sec-01, #ddd);
  border-radius: 4px;
  background: var(--gray-xl, #fafafa);
`;

// ─── Read-only styled components ─────────────────────────────────────────────

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReadOnlyValue = styled.div`
  color: var(--black, #333);
  white-space: pre-wrap;
`;
